import { c as _c } from "react/compiler-runtime";
import BubbleButtonComponent, { BubbleType } from '@spa/components/bubble-button/bubble-button.component';
import React, { ReactElement, startTransition, useRef, useState } from 'react';
import { ButtonType } from '@spa/components/button/types/ButtonType';
import ConfigurableCmsComponentMetadata from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-component-metadata.component';
import ConfigurableCmsSlotMetadata from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-slot-metadata.component';
import ConfigurableCmsValidity from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-validity.component';
import ErroredComponent from '@spa/components/errored/errored.component';
import HeadlineComponent from '../../headline/headline.component';
import LazyRenderingProps from '../../lazy-and-dynamic/lazyAndDynamicComponentFactory.pops';
import LinkButtonComponent from '@spa/components/button/link-button/link-button.component';
import { LinkComponentLink } from '../../link/link.component';
import { Metadata } from '@spa/pages-converters/cms/component-envelops/CmsComponentEnvelopBase';
import ProductCarouselComponent from '../product-carousel/product-carousel.component';
import { Props as ProductCarouselProps } from '../product-carousel/product-carousel.component.props';
import { Product as ProductTileProduct } from '../../product-list/product-tile/product-tile.component';
import { SearchTrackingHistoryContext } from '@spa/lib/tracking/useSearchResultTracking';
import classNames from 'clsx';
import styles from './tabbed-carousel.component.module.scss';
export type Product = ProductTileProduct;
export interface TabButton {
  readonly text: string;
  readonly style: ButtonType;
  readonly link: LinkComponentLink;
}
export interface CarouselTab {
  readonly pageSize: number;
  readonly products: ReadonlyArray<Product>;
  readonly totalCount: number;
  readonly searchMetadata?: ProductCarouselProps['searchMetadata'];
  readonly headline: string;
  readonly button: TabButton | null;
  readonly metadata: ReadonlyArray<Metadata>;
  readonly hidden?: boolean;
}
export interface Props extends LazyRenderingProps {
  readonly tabs: ReadonlyArray<CarouselTab>;
  readonly cmsPath: string;
  readonly componentId: string;
  readonly headline: string | null;
  readonly subHeadline: string | null;
  readonly headlineLink: LinkComponentLink | null;
  readonly tabsMetadata: ReadonlyArray<Metadata>;
  readonly dateTo: number | null;
  readonly dateFrom: number | null;
  readonly priority: boolean;
}
interface TabbedCarousel {
  readonly carousel: ReactElement;
  readonly button: ReactElement | null;
  readonly metadata: ReadonlyArray<Metadata>;
}
interface Tabs {
  readonly buttons: ReadonlyArray<ReactElement>;
  readonly carousels: ReadonlyArray<TabbedCarousel>;
}
function TabbedCarousel(t0) {
  const $ = _c(57);
  const {
    tabs,
    headline,
    subHeadline,
    headlineLink,
    componentId,
    cmsPath,
    lazyRenderingMode,
    editorMode,
    tabsMetadata,
    dateTo,
    dateFrom,
    priority
  } = t0;
  const [selectedTab, setSelectedTab] = useState(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const searchTrackingHistory = useRef(t1);
  let t2;
  if ($[1] !== cmsPath || $[2] !== componentId || $[3] !== editorMode || $[4] !== lazyRenderingMode || $[5] !== priority || $[6] !== selectedTab || $[7] !== tabs) {
    t2 = () => {
      const targetButtons = [];
      const targetCarousels = [];
      tabs.filter(t3 => {
        const {
          products,
          hidden
        } = t3;
        return (editorMode || !hidden) && (editorMode || products.length > 0);
      }).forEach((t4, tabIdx) => {
        const {
          products: products_0,
          pageSize,
          totalCount,
          headline: headline_0,
          button,
          metadata,
          hidden: hidden_0,
          searchMetadata
        } = t4;
        const targetButton = <BubbleButtonComponent text={headline_0} isHighlighted={tabIdx === selectedTab} key={tabIdx} onClick={() => startTransition(() => {
          setSelectedTab(tabIdx);
        })} type={BubbleType.BUTTON} />;
        const targetCarousel = hidden_0 ? <ErroredComponent error={`Failed to load product carousel tab: ${headline_0}`} editorMode={editorMode} type="ProductCarouselTab" id={`${componentId}-${tabIdx}`} key={headline_0 ?? tabIdx} /> : <ProductCarouselComponent componentId={componentId} products={products_0} cmsPath={cmsPath} pageSize={pageSize} totalCount={totalCount} searchMetadata={searchMetadata} editorMode={editorMode} lazyRenderingMode={lazyRenderingMode} tabIndex={tabIdx} key={headline_0 ?? tabIdx} title={null} priority={priority && tabIdx === 0} />;
        const targetBottomButton = button && !hidden_0 ? <div key={button.text} className={styles["tabbed-carousel__button-wrapper"]}><LinkButtonComponent buttonType={button.style} text={button.text} link={button.link} iconLeft={{
            src: "/assets/icons/loyalty.svg",
            alt: "loyalty-icon",
            width: 24,
            height: 24,
            unoptimized: true
          }} iconRight={{
            src: "/assets/icons/arrow_forward_white_long.svg",
            alt: "arrow-icon",
            width: 24,
            height: 24,
            unoptimized: true
          }} /></div> : null;
        targetCarousels.push({
          carousel: targetCarousel,
          button: targetBottomButton,
          metadata
        });
        targetButtons.push(targetButton);
      });
      return {
        buttons: targetButtons,
        carousels: targetCarousels
      };
    };
    $[1] = cmsPath;
    $[2] = componentId;
    $[3] = editorMode;
    $[4] = lazyRenderingMode;
    $[5] = priority;
    $[6] = selectedTab;
    $[7] = tabs;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const generateTabs = t2;
  let T0;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[9] !== componentId || $[10] !== dateFrom || $[11] !== dateTo || $[12] !== editorMode || $[13] !== generateTabs || $[14] !== headline || $[15] !== headlineLink || $[16] !== selectedTab || $[17] !== subHeadline || $[18] !== tabsMetadata) {
    const {
      buttons,
      carousels
    } = generateTabs();
    T0 = SearchTrackingHistoryContext.Provider;
    t9 = searchTrackingHistory;
    t7 = styles["tabbed-carousel"];
    if ($[27] !== componentId || $[28] !== headline || $[29] !== headlineLink || $[30] !== subHeadline) {
      t8 = headline && <HeadlineComponent id={`${componentId}-headline`} headline={headline} subHeadline={subHeadline} link={headlineLink} />;
      $[27] = componentId;
      $[28] = headline;
      $[29] = headlineLink;
      $[30] = subHeadline;
      $[31] = t8;
    } else {
      t8 = $[31];
    }
    t3 = styles["tabbed-carousel"];
    if ($[32] !== dateFrom || $[33] !== dateTo || $[34] !== editorMode || $[35] !== tabsMetadata) {
      t4 = editorMode && <><ConfigurableCmsSlotMetadata beforeSlot={true} metadata={tabsMetadata} /><ConfigurableCmsValidity dateFrom={dateFrom} dateTo={dateTo} /></>;
      $[32] = dateFrom;
      $[33] = dateTo;
      $[34] = editorMode;
      $[35] = tabsMetadata;
      $[36] = t4;
    } else {
      t4 = $[36];
    }
    t5 = buttons.length > 1 && <div className={styles["tabbed-carousel__tabs-wrapper"]}>{buttons}</div>;
    let t10;
    if ($[37] !== editorMode || $[38] !== selectedTab) {
      t10 = (t11, tabIndex) => {
        const {
          metadata: metadata_0,
          carousel,
          button: button_0
        } = t11;
        const cmsMetadataClassnames = tabIdx_0 => classNames(styles["tabbed-carousel__metadata"], {
          [styles["tabbed-carousel__metadata--show"]]: tabIdx_0 === selectedTab
        });
        const shouldRenderCarouselWrapper = editorMode || selectedTab === tabIndex;
        return shouldRenderCarouselWrapper && <div className={styles["tabbed-carousel__carousels-wrapper"]} key={tabIndex}>{editorMode && <div className={cmsMetadataClassnames(tabIndex)}><ConfigurableCmsComponentMetadata beforeComponent={true} metadata={metadata_0} /></div>}{selectedTab === tabIndex && <>{carousel}{button_0}</>}{editorMode && <div className={cmsMetadataClassnames(tabIndex)}><ConfigurableCmsComponentMetadata beforeComponent={false} metadata={metadata_0} /></div>}</div>;
      };
      $[37] = editorMode;
      $[38] = selectedTab;
      $[39] = t10;
    } else {
      t10 = $[39];
    }
    t6 = carousels.map(t10);
    $[9] = componentId;
    $[10] = dateFrom;
    $[11] = dateTo;
    $[12] = editorMode;
    $[13] = generateTabs;
    $[14] = headline;
    $[15] = headlineLink;
    $[16] = selectedTab;
    $[17] = subHeadline;
    $[18] = tabsMetadata;
    $[19] = T0;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    T0 = $[19];
    t3 = $[20];
    t4 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  let t10;
  if ($[40] !== editorMode || $[41] !== tabsMetadata) {
    t10 = editorMode && <ConfigurableCmsSlotMetadata beforeSlot={false} metadata={tabsMetadata} />;
    $[40] = editorMode;
    $[41] = tabsMetadata;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== t10 || $[44] !== t3 || $[45] !== t4 || $[46] !== t5 || $[47] !== t6) {
    t11 = <div className={t3}>{t4}{t5}{t6}{t10}</div>;
    $[43] = t10;
    $[44] = t3;
    $[45] = t4;
    $[46] = t5;
    $[47] = t6;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  let t12;
  if ($[49] !== t11 || $[50] !== t7 || $[51] !== t8) {
    t12 = <div className={t7}>{t8}{t11}</div>;
    $[49] = t11;
    $[50] = t7;
    $[51] = t8;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] !== T0 || $[54] !== t12 || $[55] !== t9) {
    t13 = <T0 value={t9}>{t12}</T0>;
    $[53] = T0;
    $[54] = t12;
    $[55] = t9;
    $[56] = t13;
  } else {
    t13 = $[56];
  }
  return t13;
}
export default TabbedCarousel;